// BETA 15.26 — Character passive system.
//
// Two layers per character:
//
//   LAYER 1 (per-level passive): a single stat that scales with player level.
//     Applied per-frame in itemsAdapter.applyAllBonusesToPlayer. Each character
//     gets a different stat — no two characters share the same Layer 1 stat
//     so each character has a distinct natural growth curve.
//
//   LAYER 2 (signature ability): the unique mechanic that defines the character
//     in play. Some are pure stat scaling (Berserker Rage), some are event
//     triggers (Silly Juice on kill / reset on damage), some are projectile
//     modifiers (Quick Draw, King's Court). Each is wired to the simplest
//     possible hook point.
//
// Run state for Layer 2 lives in `state.character` lookups + module-level
// counters on this file (kept here to centralize per-character logic).

import type { GameState, Player, PlayerId } from './types';
// BETA 20.72 — Silly Juice stack VFX (gain motes / spill flash)
import { spawnParticles } from './types';

// ─── Layer 1 — per-level passive stats ─────────────────────────────────
// Each entry: { stat: which Player field, perLevel: how much per player level }
// Stats use the same names as Player fields so the apply is one line.

export type Layer1Stat =
  | 'critChance'         // Tactical
  | 'armor'              // Sponge (flat add, NOT %)
  | 'critDamage'         // Cowboy
  | 'damageBonus'        // Killa
  | 'attackAreaBonus'    // Wizard ("Attack Size")
  | 'attackSpeedBonus'   // Viking
  | 'luck'               // Mr Silly
  | 'knockbackBonus'     // LeBron
  | 'pickupRange'        // Chad
  | 'goldMultPct';       // Pirate (special — applied as multiplier conversion)

export interface CharacterPassiveDef {
  id: string;
  // Layer 1
  layer1Stat: Layer1Stat;
  layer1PerLevel: number;        // amount per player level
  layer1Label: string;           // e.g. "+1% Crit Chance per level" — for menu
  // Layer 2
  signatureName: string;         // e.g. "Sniper's Eye"
  signatureDesc: string;         // 1-line description for menu
}

export const CHARACTER_PASSIVES: Record<string, CharacterPassiveDef> = {
  tactical: {
    id: 'tactical',
    layer1Stat: 'critChance', layer1PerLevel: 1,
    layer1Label: '+1% Crit Chance per level',
    signatureName: "Sniper's Eye",
    signatureDesc: 'Crits deal +30% bonus damage',
  },
  sponge: {
    id: 'sponge',
    // BETA 17.10 — PSBOT cycle-30z + cycle-30aa: Sponge collapsed 167s → 62s
    // median survival post 16.91 spawn rebalance (×2 HP × 1.55 dmg enemies).
    // Sponge's identity = "tank lots of damage" so the rebalance hits it
    // disproportionately. Compensating: layer1 +1 → +1.5 armor per level
    // (at lvl 33 = +49.5 armor vs old +33). Signature stack 8 → 12 armor
    // (max 3 stacks → +36 vs old +24). Total max armor swing: +85.5 vs
    // old +57 at lvl 33 with full sig stacks. Should restore Sponge to
    // 120-150s survival range against new enemies.
    layer1Stat: 'armor', layer1PerLevel: 1.5,
    layer1Label: '+1.5 Armor per level',
    signatureName: 'Sponge Absorb',
    signatureDesc: '+12 Armor per hit (max 3 stacks, 3s)',
  },
  cowboy: {
    id: 'cowboy',
    layer1Stat: 'critDamage', layer1PerLevel: 1,
    layer1Label: '+1% Crit Damage per level',
    signatureName: 'Quick Draw',
    signatureDesc: 'First shot of every burst: +25% dmg + guaranteed crit',
  },
  killa: {
    id: 'killa',
    // BETA 16.20 — PSBOT cycle-23: 1 → 0.5. Killa's was the ONLY direct-
    // damage layer-1 passive in the roster; everyone else gives utility,
    // economy, or defense. At lvl 39 it contributed +39% damage on top of
    // his +50% Suppression signature — runaway scaling. Halving brings
    // his passive contribution to +19.5% at lvl 39, still strongest
    // direct-damage layer-1 but no longer god-mode.
    layer1Stat: 'damageBonus', layer1PerLevel: 0.5,
    layer1Label: '+0.5% Damage per level',
    signatureName: 'Suppression Fire',
    signatureDesc: '+5% damage per second of continuous fire (max +50%)',
  },
  wizard: {
    id: 'wizard',
    layer1Stat: 'attackAreaBonus', layer1PerLevel: 1,
    layer1Label: '+1% Attack Size per level',
    signatureName: 'Arcane Surge',
    signatureDesc: 'Every 5 kills: 4s of 1.75× damage + 50% bigger + 1 pierce',
  },
  viking: {
    id: 'viking',
    layer1Stat: 'attackSpeedBonus', layer1PerLevel: 1,
    layer1Label: '+1% Attack Speed per level',
    signatureName: 'Berserker Rage',
    signatureDesc: 'HP-scaled: up to +40% atk speed / +25% damage as HP drops',
  },
  mr_silly: {
    id: 'mr_silly',
    // BETA 16.27 — PSBOT cycle-30 C.1: layer1PerLevel 2 → 2.5. Per cycle-30
    // pacing audit: only Mr Silly's luck-passive prevents him from
    // medianing 0% luck. Modest bump so he can stack into the 100%+
    // threshold via passive alone in long runs.
    layer1Stat: 'luck', layer1PerLevel: 2.5,
    layer1Label: '+2.5% Luck per level',
    signatureName: 'Silly Juice',
    signatureDesc: '+5% damage per kill (max 25 stacks). Halved when hit.',
  },
  lebron: {
    id: 'lebron',
    layer1Stat: 'knockbackBonus', layer1PerLevel: 2,
    layer1Label: '+2% Knockback per level',
    signatureName: "King's Court",
    signatureDesc: 'Basketball deals 1.25× damage per bounce',
  },
  chad: {
    id: 'chad',
    layer1Stat: 'pickupRange', layer1PerLevel: 2,
    layer1Label: '+2% Pickup Range per level',
    signatureName: 'Golden Aura',
    signatureDesc: 'Aura grows +15wu/lvl. +25% extra gold from kills in aura.',
  },
  pirate: {
    id: 'pirate',
    layer1Stat: 'goldMultPct', layer1PerLevel: 1,
    layer1Label: '+1% Gold Multiplier per level',
    // BETA 16.51 — PSBOT cycle-30k: Pirate buff (cycle-30j) lifted weapon
    // damage but kill rate only marginally improved (0.5 → 0.61) because
    // Pirate has no character damage signature. Adding "Pirate's Greed"
    // gold→damage scaling. Scavenger layer-2 (extra drops) stays — Greed
    // is a SECOND layer-2 buff that stacks alongside.
    signatureName: "Pirate's Greed",
    signatureDesc: '+1% damage per 100 gold collected, capped at +25%. Scavenger drops still apply.',
  },
};

// ─── Layer 2 runtime state (per-player store, reset per run) ────────────
// Centralizing here so the character-passive logic is one file. Each
// character's signature only reads/writes its own fields. Reset via
// resetCharacterPassiveRunState() at startNewRun.
//
// BETA 23.62 — CO-OP PHASE 1: what used to be ~39 module-level `let`s is now
// ONE PassiveRunState store per player. `S` is the ACTIVE player's store —
// all signature logic below reads/writes S.<field>, exactly the same values
// with the same lifecycles as the old singletons. Single-player never
// switches stores, so behavior is identical. In co-op the engine calls
// setActiveCharacterPassivePlayer(id) before running a player's frame slice.
interface PassiveRunState {
  // Sponge Absorb: +8 armor per hit, 3s timer, max 3 stacks
  spongeStacks: number;
  spongeStackUntil: number;       // game time when current stacks expire

  // Killa Suppression Fire: continuous-fire stack timer
  killaSuppressSeconds: number;   // 0..10 (capped — 5%/sec, 10s = +50%)
  killaLastFireTime: number;      // game time of last fire event
  killaPeakSuppressSec: number;   // peak reached this run (tracker)
  // BETA 16.87 — Per Dylan: "i should not have 0 procs on killa." BETA 16.84
  // switched getSignatureProcsThisRun to surface Math.round(killaPeakSuppressSec)
  // — which CAN read 0 for short runs (peak < 0.5s) or runs where the
  // state.time vs state.elapsedTime sync window misses. Now: in addition to
  // peak-seconds tracking, count EVERY fire event for Killa as a "proc."
  // This guarantees a non-zero proc count for any Killa run that fires at
  // least one shot, which is the actionable signal PSBOT needs to verify
  // "is this signature firing at all" vs "is it firing strongly." Peak
  // stacks (continuous-uptime quality) is still surfaced via
  // getSignatureMaxStacksThisRun.
  killaFireEventsThisRun: number;

  // Silly Juice: kill stacks, reset on damage
  // (Note: there's also an item with the same mechanic. The character passive
  // version uses a separate counter so they stack additively if both equipped.)
  sillyCharStacks: number;
  sillyCharPeakStacks: number;    // peak this run
  // BETA 16.84 — PSBOT cycle-30k flagged Mr Silly as "0 procs in bot data."
  // Root cause: getSignatureProcsThisRun returned 0 by design for continuous
  // chars, so PSBOT couldn't see ANY signal in the proc column. Track total
  // kill-stacks-gained so the proc column shows activity.
  sillyCharProcsThisRun: number;

  // BETA 16.84 — Viking signature proc tracker (was missing entirely).
  // Counts the number of times missingFrac (HP fraction missing) crosses
  // the 0.25 threshold from below — i.e. each "rage activation" when HP
  // drops below 75%. Resets when player heals back above the threshold.
  // Surfaces non-zero proc count to PSBOT for runs where Viking gets hurt.
  // BETA 16.87 — added vikingActiveTicks: increments every frame while
  // rage is active, and bumps vikingProcsThisRun once per 60 frames of
  // active rage (~1 proc/sec) so tank builds that hover low-HP without
  // crossing thresholds still surface meaningful proc count.
  vikingProcsThisRun: number;
  vikingPrevRageActive: boolean;
  vikingActiveTicks: number;

  // Wizard Arcane Surge: kill counter + 4s damage window
  wizKillsSinceSurge: number;
  wizSurgeUntil: number;          // game time when surge ends
  wizSurgeProcsThisRun: number;
  wizSurgeSecondsThisRun: number;

  // Cowboy Quick Draw: charge flag. Set on each fire-event start, consumed
  // by the first projectile spawn of that fire.
  cowboyDrawArmed: boolean;
  cowboyDrawProcsThisRun: number;
  // BETA 16.24 — Quick Draw 0.5s cooldown gate timestamp (see
  // COWBOY_QUICK_DRAW_CD_SEC at onPlayerFireProjectile).
  cowboyLastQdTime: number;

  // Sniper's Eye procs (just count crits while character is Tactical)
  sniperProcsThisRun: number;

  // Sponge Absorb proc count
  spongeProcsThisRun: number;
  spongePeakStacks: number;       // peak armor stacks this run

  // Pirate Scavenger drop counts
  scavengerCrateDrops: number;
  scavengerHealthDrops: number;
  // BETA 16.51 — Pirate's Greed peak (gold/100, capped 25). Tracked for
  // telemetry verification across cycle-31 bot runs.
  pirateGreedPeakStacks: number;

  // Chad Golden Aura gold drop count
  chadGoldDropsInAura: number;

  // LeBron King's Court — bounce-chain damage tracker
  lebronBonusDamageTotal: number;

  // BETA 19.93 — Per-character signature damage credit (Dylan ask via PSBOT
  // cycle-30bb). Each char's signature adds some % to p.damageBonus during
  // its active window. We snapshot that % each frame; engine then attributes
  // `deltaFrameDamage × sigContribPct / 100` to the per-char counter. Result:
  // every signature now produces a meaningful signatureBonusDamage value in
  // CSV (was LeBron-only — only writer to its own counter).
  currentSignatureContribPct: number;  // computed each frame in applyCharacterPassive
  killaSignatureBonusDmg: number;
  pirateSignatureBonusDmg: number;
  wizardSignatureBonusDmg: number;
  vikingSignatureBonusDmg: number;
  sillySignatureBonusDmg: number;
  spongeSignatureBonusDmg: number;     // armor doesn't add damage but track activation
  chadSignatureBonusDmg: number;       // aura does damage via weapon, not signature
  cowboySignatureBonusDmg: number;
  tacticalSignatureBonusDmg: number;

  // Pin the prior phase for damage-on-self detection
  prevPlayerHp: number;

  // Current character ID — set by engine.ts each frame in updateGame so modules
  // without GameState access (weaponSystem) can call our hooks. Cleared on run end.
  // (Per-player: in co-op every player picks their own character.)
  currentCharId: string;
}

// Initial values = exactly the old module-let initializers.
function freshPassiveRunState(): PassiveRunState {
  return {
    spongeStacks: 0,
    spongeStackUntil: 0,
    killaSuppressSeconds: 0,
    killaLastFireTime: -10,
    killaPeakSuppressSec: 0,
    killaFireEventsThisRun: 0,
    sillyCharStacks: 0,
    sillyCharPeakStacks: 0,
    sillyCharProcsThisRun: 0,
    vikingProcsThisRun: 0,
    vikingPrevRageActive: false,
    vikingActiveTicks: 0,
    wizKillsSinceSurge: 0,
    wizSurgeUntil: 0,
    wizSurgeProcsThisRun: 0,
    wizSurgeSecondsThisRun: 0,
    cowboyDrawArmed: false,
    cowboyDrawProcsThisRun: 0,
    cowboyLastQdTime: -Infinity,
    sniperProcsThisRun: 0,
    spongeProcsThisRun: 0,
    spongePeakStacks: 0,
    scavengerCrateDrops: 0,
    scavengerHealthDrops: 0,
    pirateGreedPeakStacks: 0,
    chadGoldDropsInAura: 0,
    lebronBonusDamageTotal: 0,
    currentSignatureContribPct: 0,
    killaSignatureBonusDmg: 0,
    pirateSignatureBonusDmg: 0,
    wizardSignatureBonusDmg: 0,
    vikingSignatureBonusDmg: 0,
    sillySignatureBonusDmg: 0,
    spongeSignatureBonusDmg: 0,
    chadSignatureBonusDmg: 0,
    cowboySignatureBonusDmg: 0,
    tacticalSignatureBonusDmg: 0,
    prevPlayerHp: 100,
    currentCharId: '',
  };
}

const _passiveStores: Partial<Record<PlayerId, PassiveRunState>> = {};
let _activePassivePlayerId: PlayerId = 'p1';
// S = the ACTIVE player's store. Every signature read/write below goes
// through it. Single-player: always p1's store.
let S: PassiveRunState = (_passiveStores.p1 = freshPassiveRunState());

/** CO-OP PHASE 2+: point the passive system at a player before simulating
 *  their slice of the frame. Creates the store lazily on first switch.
 *  Single-player never calls this. */
export function setActiveCharacterPassivePlayer(id: PlayerId): void {
  _activePassivePlayerId = id;
  S = _passiveStores[id] ?? (_passiveStores[id] = freshPassiveRunState());
}
export function getCurrentSignatureContribPct(): number { return S.currentSignatureContribPct; }
export function attributeSignatureFrameDamage(deltaDmg: number): void {
  if (S.currentSignatureContribPct <= 0 || deltaDmg <= 0) return;
  const credit = deltaDmg * S.currentSignatureContribPct / 100;
  switch (S.currentCharId) {
    case 'killa':    S.killaSignatureBonusDmg    += credit; break;
    case 'pirate':   S.pirateSignatureBonusDmg   += credit; break;
    case 'wizard':   S.wizardSignatureBonusDmg   += credit; break;
    case 'viking':   S.vikingSignatureBonusDmg   += credit; break;
    case 'mr_silly': S.sillySignatureBonusDmg    += credit; break;
    case 'sponge':   S.spongeSignatureBonusDmg   += credit; break;
    case 'chad':     S.chadSignatureBonusDmg     += credit; break;
    case 'cowboy':   S.cowboySignatureBonusDmg   += credit; break;
    case 'tactical': S.tacticalSignatureBonusDmg += credit; break;
  }
}

// BETA 20.88 — PSBOT cycle-30bg §2: Cowboy Quick Draw is an INSTANT per-shot
// proc (+25% on the burst's first shot), not a sustained-% window — so the
// frame-sampled attributeSignatureFrameDamage above never sees it and Cowboy's
// signatureBonusDamage stayed 0 (2080 procs, 0 credit). Credit it directly at
// the per-shot site: the +25% bonus = shotBaseDamage × 0.25. (No double-count —
// nothing sets S.currentSignatureContribPct for cowboy, so the frame path is a
// no-op for him.) Telemetry-only; the +25%/crit already applies in gameplay.
export function creditCowboyQuickDraw(shotBaseDamage: number): void {
  if (S.currentCharId === 'cowboy' && shotBaseDamage > 0) {
    S.cowboySignatureBonusDmg += shotBaseDamage * 0.25;
  }
}

export function setCurrentCharacterId(id: string): void { S.currentCharId = id; }

export function resetCharacterPassiveRunState(): void {
  // BETA 23.62 — CO-OP PHASE 1: a new run keeps only the active player's
  // store; leftover co-op stores from a previous run are dropped (in
  // single-player none exist, so this is a no-op). The active store is
  // then field-reset below exactly as before — including leaving
  // currentCharId untouched, which the pre-23.62 reset also did.
  for (const k of Object.keys(_passiveStores) as PlayerId[]) {
    if (k !== _activePassivePlayerId) delete _passiveStores[k];
  }
  S.spongeStacks = 0; S.spongeStackUntil = 0; S.spongeProcsThisRun = 0; S.spongePeakStacks = 0;
  S.killaSuppressSeconds = 0; S.killaLastFireTime = -10; S.killaPeakSuppressSec = 0; S.killaFireEventsThisRun = 0;
  S.sillyCharStacks = 0; S.sillyCharPeakStacks = 0; S.sillyCharProcsThisRun = 0;
  S.vikingProcsThisRun = 0; S.vikingPrevRageActive = false; S.vikingActiveTicks = 0;
  S.wizKillsSinceSurge = 0; S.wizSurgeUntil = 0; S.wizSurgeProcsThisRun = 0; S.wizSurgeSecondsThisRun = 0;
  S.cowboyDrawArmed = false; S.cowboyDrawProcsThisRun = 0; S.cowboyLastQdTime = -Infinity;
  S.sniperProcsThisRun = 0;
  S.scavengerCrateDrops = 0; S.scavengerHealthDrops = 0;
  S.pirateGreedPeakStacks = 0;
  S.chadGoldDropsInAura = 0;
  S.lebronBonusDamageTotal = 0;
  S.prevPlayerHp = 100;
  // BETA 19.93 — reset per-char signature damage credit accumulators
  S.currentSignatureContribPct = 0;
  S.killaSignatureBonusDmg = 0; S.pirateSignatureBonusDmg = 0; S.wizardSignatureBonusDmg = 0;
  S.vikingSignatureBonusDmg = 0; S.sillySignatureBonusDmg = 0; S.spongeSignatureBonusDmg = 0;
  S.chadSignatureBonusDmg = 0; S.cowboySignatureBonusDmg = 0; S.tacticalSignatureBonusDmg = 0;
}

// ─── Apply Layer 1 + Layer 2 stat-scaling (called per frame in itemsAdapter) ───

/**
 * Apply both layers of the character's passive to the player. Called from
 * applyAllBonusesToPlayer at the END of all other applies (so passives stack
 * on top of items/percs/shrines, never get overwritten).
 *
 * This handles BOTH Layer 1 (per-level stat) AND Layer 2 stat-shaped abilities
 * (Berserker Rage, Sponge Absorb stacks, Suppression Fire stacks, Silly Juice
 * stacks, Sniper's Eye crit damage bonus, Golden Aura radius). Event-driven
 * Layer 2 abilities (Quick Draw, Arcane Surge, Scavenger, King's Court) are
 * wired via separate hooks.
 */
export function applyCharacterPassive(state: GameState, p: Player): void {
  const charId = state.character?.id;
  if (!charId) return;
  const def = CHARACTER_PASSIVES[charId];
  if (!def) return;

  const lvl = Math.max(1, p.level ?? 1);
  const layer1Total = def.layer1PerLevel * lvl;

  // ── Layer 1: per-level stat add ──
  switch (def.layer1Stat) {
    case 'critChance':       p.critChance       += layer1Total; break;
    case 'armor':            p.armor            += layer1Total; break;     // flat add (Sponge)
    case 'critDamage':       p.critDamage       += layer1Total; break;
    case 'damageBonus':      p.damageBonus      += layer1Total; break;
    case 'attackAreaBonus':  p.attackAreaBonus  += layer1Total; break;
    case 'attackSpeedBonus': p.attackSpeedBonus += layer1Total; break;
    case 'luck':             p.luck             += layer1Total; break;
    case 'knockbackBonus':   p.knockbackBonus   += layer1Total; break;
    case 'pickupRange':      p.pickupRange      += layer1Total; break;
    case 'goldMultPct':
      // goldMultiplier is a fraction (1.0 = +0%, 1.5 = +50%). Convert the
      // per-level integer-percent to fraction multiply.
      p.goldMultiplier *= 1 + (layer1Total / 100);
      break;
  }

  // ── Layer 2: stat-shaped signatures ──
  const time = state.time ?? 0;
  // BETA 19.93 — reset per-frame so each character's case can write its
  // own contribution; engine attributes deltaFrameDamage × this % to the
  // per-char signature counter.
  S.currentSignatureContribPct = 0;

  // Sniper's Eye — Tactical: +30 to critDamage (the "+30% bonus dmg on crit"
  // simplification — applies as extra crit multiplier on the same hit).
  if (charId === 'tactical') {
    p.critDamage += 30;
    // Tactical's sig adds critDmg not damageBonus, but it boosts damage on
    // crits. Approximate contrib as +30% of damage × crit chance.
    S.currentSignatureContribPct = 30 * ((p.critChance ?? 0) / 100);
  }

  // Sponge Absorb — Sponge: +12 armor per stack (BETA 17.10, was +8).
  // Stacks expire if no hit for 3s — checked here per frame (cheap).
  // BETA 19.57 — Stacks should NOT expire during non-playing phases
  // (chest_spin, levelup, shrine_choice). During a 2s chest pickup,
  // S.spongeStackUntil falls behind state.time → stacks reset to 0
  // even though the player hasn't actually been idle in combat. Gate
  // the expiry on isPlayingPhase to fix the bleed.
  if (charId === 'sponge') {
    if (state.phase === 'playing' && time > S.spongeStackUntil) S.spongeStacks = 0;
    p.armor += 12 * S.spongeStacks;
  }

  // Suppression Fire — Killa: +5% damage per second of continuous fire,
  // capped at 10s = +50%. Decay handled in tickKillaSuppressionFire.
  if (charId === 'killa') {
    const killaContrib = Math.min(50, S.killaSuppressSeconds * 5);
    p.damageBonus += killaContrib;
    S.currentSignatureContribPct = killaContrib;
  }

  // BETA 16.51 — Pirate's Greed: +1% damage per 100 gold collected, capped
  // at +25%. PSBOT cycle-30j §4 spec: "the gold-focused identity feel
  // REWARDING — Pirate farms gold AND gets stronger." Pulls live gold
  // count off the player so the bonus rises and falls naturally as the
  // player spends gold on chests.
  if (charId === 'pirate') {
    const goldStacks = Math.min(25, Math.floor((p.gold ?? 0) / 100));
    p.damageBonus += goldStacks;
    S.currentSignatureContribPct = goldStacks;
    if (goldStacks > 0 && goldStacks > S.pirateGreedPeakStacks) {
      S.pirateGreedPeakStacks = goldStacks;
    }
  }

  // Wizard Arcane Surge: during Surge window, apply burst-style buffs.
  // Tracking time-active accumulated in tick fn.
  if (charId === 'wizard' && time < S.wizSurgeUntil) {
    p.damageBonus      += 75;   // 1.75× damage = +75%
    p.attackAreaBonus  += 50;   // +50% projectile size
    S.currentSignatureContribPct = 75;
    // Pierce bonus is via lazy field (matches itemsAdapter pattern)
    const pAny = p as unknown as Record<string, number>;
    pAny.projectilePierceBonus = (pAny.projectilePierceBonus ?? 0) + 1;
  }

  // Berserker Rage — Viking: HP-scaled atk speed + dmg.
  // 0% HP missing = no bonus; 100% HP missing (= 0 HP) = max bonus.
  // BETA 15.54 — softened per PSBOT cycle-20 audit. Long-run Viking +
  // Berserker at low HP was the source of every cycle's atk-speed/damage
  // outliers (cycle-19 = 120%, cycle-20 = 96%/189%). Per BALANCING_PHILOSOPHY:
  // tune magnitudes, not caps. Reduced atk-speed scaling 60% → 40% and
  // damage scaling 40% → 25%. Still strong at low HP, doesn't trivialize
  // bosses or push outliers past the upgrade-system targets.
  if (charId === 'viking') {
    const missingFrac = Math.max(0, 1 - (p.hp / Math.max(1, p.maxHp)));
    p.attackSpeedBonus += 40 * missingFrac;  // BETA 15.54: 60 → 40
    p.damageBonus      += 25 * missingFrac;  // BETA 15.54: 40 → 25
    S.currentSignatureContribPct = 25 * missingFrac;
    // BETA 16.84 — count rage activations (missingFrac threshold crossings).
    // Edge-trigger: only on the frame HP drops below 75%, not every frame
    // it stays low. Heal back above to re-arm. Surfaces non-zero proc
    // count for PSBOT data analysis.
    // BETA 16.87 — also tick the proc counter every 60th frame while
    // rage is active. So a tank build that bottoms out at 60% HP for
    // 2 minutes (zero threshold crossings, but signature is firing
    // hard the whole time) still reports meaningful proc count
    // proportional to active duration.
    const isRageActive = missingFrac > 0.25;
    if (isRageActive && !S.vikingPrevRageActive) S.vikingProcsThisRun++;
    S.vikingPrevRageActive = isRageActive;
    if (isRageActive) {
      S.vikingActiveTicks = (S.vikingActiveTicks + 1) % 60;
      if (S.vikingActiveTicks === 0) S.vikingProcsThisRun++;
    }
  }

  // Silly Juice — Mr Silly: +5% damage per kill stack, max 25 stacks.
  if (charId === 'mr_silly') {
    p.damageBonus += 5 * S.sillyCharStacks;
    S.currentSignatureContribPct = 5 * S.sillyCharStacks;
  }

  // Golden Aura — Chad: write radius bonus into ultraState so the Chad Aura
  // weapon system reads it and grows the ring. +15wu per player level.
  if (charId === 'chad') {
    const ultraState = (p as unknown as { ultraState?: Record<string, number> }).ultraState;
    if (ultraState) {
      ultraState.chadAuraBonus = lvl * 15;
    } else {
      (p as unknown as { ultraState: Record<string, number> }).ultraState = { chadAuraBonus: lvl * 15 };
    }
  }
}

// ─── Layer 2 event hooks ────────────────────────────────────────────────

/**
 * Called when an enemy is killed by the player. enemyX/Y is the enemy
 * position at death (used by Chad Golden Aura to check in-aura).
 *
 * Returns extra drops to add to the enemy's normal loot (Pirate Scavenger
 * gives +GOOP crate/+health orb, Chad gives +gold). The damageEnemy caller
 * appends these to the standard drops table.
 */
// Returns extra loot drops the kill site should APPEND to the enemy's normal
// rollEnemyDrops() output. Shape matches EnemyLootDrop (type/chance/amount).
// chance=1.0 because we already rolled the probability here.
export interface ExtraDrop { type: 'goop' | 'health'; chance: number; amount: number }

export function onPlayerKilledEnemy(state: GameState, enemyX: number, enemyY: number, isCrit: boolean): ExtraDrop[] {
  const charId = state.character?.id;
  const drops: ExtraDrop[] = [];
  if (!charId) return drops;

  // Wizard Arcane Surge: 5-kill counter
  if (charId === 'wizard') {
    S.wizKillsSinceSurge++;
    if (S.wizKillsSinceSurge >= 5) {
      S.wizKillsSinceSurge = 0;
      S.wizSurgeUntil = (state.time ?? 0) + 4;     // 4-second surge window
      S.wizSurgeProcsThisRun++;
    }
  }

  // Mr Silly Silly Juice: stack +1 on kill (capped at 25)
  if (charId === 'mr_silly') {
    if (S.sillyCharStacks < 25) {
      S.sillyCharStacks++;
      if (S.sillyCharStacks > S.sillyCharPeakStacks) S.sillyCharPeakStacks = S.sillyCharStacks;
      // BETA 20.72 — Silly Juice fills up: orange motes rise off the player
      // on each stack ACTUALLY gained (inside the <25 branch so nothing
      // spawns at cap). 2 motes early → 3 past half-stacks; kept subtle
      // because Mr Silly gains a stack on every kill.
      spawnParticles(state.particles, state.player.x, state.player.y, '#ff9933',
        S.sillyCharStacks >= 13 ? 3 : 2, {
          shape: 'mote', speedMin: 10, speedMax: 40, gravity: -90,
          lifeMin: 0.45, lifeMax: 0.8, sizeMin: 2, sizeMax: 4,
        });
    }
    // BETA 16.84 — count every kill-stack-gain event as a "proc" (so PSBOT
    // sees non-zero activity in the signatureProcsThisRun column even
    // when stacks cap at 25). Total kills with the stack-gain branch
    // taken is the closest analog to "signature fired."
    S.sillyCharProcsThisRun++;
  }

  // Tactical Sniper's Eye proc count
  if (charId === 'tactical' && isCrit) {
    S.sniperProcsThisRun++;
  }

  // Pirate Scavenger: +20% extra GOOP crate, +10% extra health orb on kill
  // BETA 15.54 — REVERTED 15.47 halve back to 5g per Dylan: "put gold back to
  // what it was as far as how much you were getting per kill."
  if (charId === 'pirate') {
    if (Math.random() < 0.2) { drops.push({ type: 'goop',   chance: 1.0, amount: 5 }); S.scavengerCrateDrops++; }  // BETA 15.54: 2 → 5 (revert)
    if (Math.random() < 0.1) { drops.push({ type: 'health', chance: 1.0, amount: 5 }); S.scavengerHealthDrops++; }
  }

  // Chad Golden Aura: +25% bonus gold drop chance if enemy was in aura.
  // Aura radius approximated as 99wu base + ultraState.chadAuraBonus.
  // BETA 15.54 — REVERTED 15.47 halve back to 5g.
  if (charId === 'chad') {
    const p = state.player;
    const auraRadius = 99 + ((p as unknown as { ultraState?: { chadAuraBonus?: number } }).ultraState?.chadAuraBonus ?? 0);
    const dx = enemyX - p.x, dy = enemyY - p.y;
    const distSq = dx * dx + dy * dy;
    if (distSq <= auraRadius * auraRadius) {
      if (Math.random() < 0.25) {
        drops.push({ type: 'goop', chance: 1.0, amount: 5 });  // BETA 15.54: 2 → 5 (revert)
        S.chadGoldDropsInAura++;
      }
    }
  }

  return drops;
}

/**
 * Called when the player takes damage (from any source). Triggers Sponge
 * Absorb stack add, Silly Juice reset.
 */
export function onPlayerTookDamage(state: GameState): void {
  const charId = state.character?.id;
  if (!charId) return;
  const time = state.time ?? 0;

  // Sponge Absorb: +1 stack, refresh 3s timer (max 3 stacks)
  if (charId === 'sponge') {
    if (S.spongeStacks < 3) S.spongeStacks++;
    if (S.spongeStacks > S.spongePeakStacks) S.spongePeakStacks = S.spongeStacks;
    S.spongeStackUntil = time + 3;
    S.spongeProcsThisRun++;
  }

  // Silly Juice: reset stacks
  // BETA 19.62 — PSBOT cycle-30aw P0: Mr Silly accounts for 10 of 17 big
  // damage bleeds (-115% drops on shrine picks). Root cause: damage events
  // fired during non-playing phases (chest_spin / levelup / shrine_choice)
  // were resetting Silly stacks to 0 → damageBonus dropped from +125% to
  // 0% by the time the next pick-snapshot ran. Same pattern as 19.57:
  // gate on isPlayingPhase. Stale damage events during pickup pauses no
  // longer reset the stack pile.
  // Also softened: reset to HALF instead of zero — Silly Juice should
  // hurt to lose, but full wipe-on-hit means the bot can never sustain
  // the bonus in long combat. Half-reset gives the player a recovery
  // window while still punishing carelessness.
  if (charId === 'mr_silly' && state.phase === 'playing') {
    // BETA 20.72 — juice SPILLED (it's a halve since 19.62, not a full
    // reset): quick red spark flash, gated so it only fires when stacks
    // were actually lost.
    if (S.sillyCharStacks > 0) {
      spawnParticles(state.particles, state.player.x, state.player.y, '#ff3322', 7, {
        shape: 'spark', speedMin: 160, speedMax: 300,
        lifeMin: 0.18, lifeMax: 0.32, stretch: 5, fadePow: 1.5,
      });
    }
    S.sillyCharStacks = Math.floor(S.sillyCharStacks / 2);
  }
}

/**
 * Called by weaponSystem when the player fires a projectile.
 * - Killa Suppression Fire: timestamp last fire (per-frame tick reads it)
 * - Cowboy Quick Draw: first projectile of every burst gets +25% dmg + crit
 *
 * Reads S.currentCharId (set by engine.ts each frame). Pass currentTimeSec
 * because weaponSystem has WeaponSystemState which carries elapsedTime
 * separately from GameState.time. isFirstBurstShot is true when this is
 * the first projectile of a new burst (caller checks weapon.currentAmmo).
 *
 * Returns whether THIS projectile is the Cowboy "draw" shot — caller
 * applies +25% damage + forces isCrit = true.
 */
// BETA 16.24 — PSBOT cycle-25 P0.S5: Quick Draw cooldown gate. Per
// cowboy run audit: 80-90% crit rate on revolver + terminator (both
// single-shot weapons). Root cause: `isFirstBurstShot` is computed as
// `weapon.currentAmmo === weapon.projectileCount`. For a 1-projectile
// weapon, EVERY shot has ammo=1=projectileCount → Quick Draw fires on
// every shot → effective 100% crit. Fix: 0.5s minimum cooldown between
// QD procs so single-shot weapons (revolver lvl 1, the_terminator) can
// proc QD at most once per 0.5s. Multi-projectile bursts (revolver
// lvl 5 = 5 shots) still proc QD on shot 1 of each burst since the
// burst-cycle reload is longer than the cooldown.
const COWBOY_QUICK_DRAW_CD_SEC = 0.5;
export function onPlayerFireProjectile(currentTimeSec: number, isFirstBurstShot: boolean): { cowboyDrawShot: boolean } {
  let cowboyDrawShot = false;
  if (!S.currentCharId) return { cowboyDrawShot };

  // Killa: any fire pumps Suppression Fire timestamp + counts as a proc.
  // BETA 16.87 — S.killaFireEventsThisRun guarantees PSBOT sees > 0 procs
  // for any Killa run that fires at least once, regardless of whether
  // S.killaSuppressSeconds ever crosses Math.round's 0.5 threshold.
  if (S.currentCharId === 'killa') {
    S.killaLastFireTime = currentTimeSec;
    S.killaFireEventsThisRun++;
  }

  // Cowboy Quick Draw: first projectile of burst is the "draw" shot,
  // gated by a 0.5s min interval so single-shot weapons can't proc on
  // every shot.
  if (S.currentCharId === 'cowboy' && isFirstBurstShot) {
    if (currentTimeSec - S.cowboyLastQdTime >= COWBOY_QUICK_DRAW_CD_SEC) {
      cowboyDrawShot = true;
      S.cowboyLastQdTime = currentTimeSec;
      S.cowboyDrawProcsThisRun++;
    }
  }

  return { cowboyDrawShot };
}

/**
 * Called every frame from the engine update loop. Handles continuous Layer 2
 * effects that need a per-frame tick:
 * - Killa Suppression Fire: timer increment / decay
 * - Wizard Arcane Surge: track active seconds
 */
export function tickCharacterPassiveRunState(state: GameState, dt: number): void {
  const charId = state.character?.id;
  if (!charId) return;
  const time = state.time ?? 0;

  // BETA 19.57 — DAMAGE-BLEED ROOT CAUSE FIX (PSBOT cycle-30av P0).
  // PSBOT found Killa's damageBonus dropping 52.5% → 3.0% between picks.
  // Root cause: Suppression Fire decays when player stops firing for >1.5s.
  // When the bot pauses for chest_spin / levelup / shrine_choice (which
  // can take 1-3s), the weapon doesn't fire → suppression decays → bonus
  // drops to near-zero. By the next snapshot, bot has barely re-built it.
  //
  // Fix: ONLY decay during 'playing' phase. During menu/pickup pauses the
  // timer holds steady so the bonus survives the pickup interruption.
  // Same logic applied to Wizard Arcane Surge tracker.
  const isPlayingPhase = state.phase === 'playing';

  // Suppression Fire: increment while firing within last 0.5s, decay otherwise.
  // BETA 19.57: skip decay when not in playing phase (chest_spin, levelup,
  // shrine_choice, paused — these freeze combat artificially).
  if (charId === 'killa' && isPlayingPhase) {
    const sinceLastFire = time - S.killaLastFireTime;
    if (sinceLastFire < 0.5) {
      // Still firing — increment timer up to 10s cap
      S.killaSuppressSeconds = Math.min(10, S.killaSuppressSeconds + dt);
      if (S.killaSuppressSeconds > S.killaPeakSuppressSec) S.killaPeakSuppressSec = S.killaSuppressSeconds;
    } else if (sinceLastFire > 1.5) {
      // Stopped firing for >1.5s — decay 1 per 0.5s = 2 per second
      S.killaSuppressSeconds = Math.max(0, S.killaSuppressSeconds - dt * 2);
    }
    // Else 0.5s < sinceLastFire <= 1.5s: hold value (no change)
  }

  // Wizard Arcane Surge: accumulate active seconds for tracker
  if (charId === 'wizard' && isPlayingPhase && time < S.wizSurgeUntil) {
    S.wizSurgeSecondsThisRun += dt;
  }
}

/**
 * Called by weaponSystem when a basketball projectile bounces toward a new
 * target. Multiplies projectile damage by 1.25 (compounding across bounces).
 * Only fires for LeBron + basketball.
 *
 * Hit 1: original damage. Hit 2 (after 1 bounce): 1.25×. Hit 3: ~1.56×.
 * Hit 6: ~3.05×. Compounds because the projectile's damage field is mutated
 * in place each bounce.
 */
export function onProjectileBounce(weaponDefId: string, currentDamage: number): number {
  if (S.currentCharId !== 'lebron') return currentDamage;
  if (weaponDefId !== 'wpn_tactical_basketball' && weaponDefId !== 'tactical_basketball') return currentDamage;
  const newDamage = Math.round(currentDamage * 1.25);
  S.lebronBonusDamageTotal += (newDamage - currentDamage);
  return newDamage;
}

// ─── Tracker accessors (read at finalizeRun) ────────────────────────────

export function getSignatureProcsThisRun(state: GameState): number {
  const charId = state.character?.id;
  switch (charId) {
    case 'tactical': return S.sniperProcsThisRun;
    case 'sponge':   return S.spongeProcsThisRun;
    case 'cowboy':   return S.cowboyDrawProcsThisRun;
    case 'wizard':   return S.wizSurgeProcsThisRun;
    case 'pirate':   return S.scavengerCrateDrops + S.scavengerHealthDrops;
    case 'chad':     return S.chadGoldDropsInAura;
    case 'lebron':   return S.lebronBonusDamageTotal > 0 ? 1 : 0;
    // BETA 16.84 — PSBOT cycle-30k+l+n+o flagged "0 procs across 4 cycles"
    // for these continuous-buff characters. The signatures DO fire; the
    // proc column just had no semantic mapping for them. Now reports
    // a meaningful activity count so PSBOT data analysis works:
    // BETA 16.87 — Per Dylan: "i should not have 0 procs on killa."
    // Switched from Math.round(S.killaPeakSuppressSec) to fire-event
    // count. Math.round of a peak < 0.5s reads 0 even if the signature
    // fired briefly. Fire-event count is > 0 for any run that fires
    // at least once — the bulletproof "did this fire at all" signal.
    // Peak suppress seconds is still on the run via
    // getSignatureMaxStacksThisRun (Killa column there is unchanged).
    case 'killa':    return S.killaFireEventsThisRun;
    case 'mr_silly': return S.sillyCharProcsThisRun;              // total stack-gain events
    // BETA 16.87 — Viking: rage-activation crossings + 1 per damage
    // event below 75% HP, so a tank build that hovers low-HP without
    // crossing the threshold still surfaces non-zero. Even a one-time
    // dip registers ≥1 proc.
    case 'viking':   return S.vikingProcsThisRun;                 // count of rage-activation crossings + per-frame ticks while active
    default:         return 0;
  }
}

export function getSignatureMaxStacksThisRun(state: GameState): number {
  const charId = state.character?.id;
  switch (charId) {
    case 'sponge':   return S.spongePeakStacks;
    case 'mr_silly': return S.sillyCharPeakStacks;
    case 'killa':    return Math.round(S.killaPeakSuppressSec);  // 0-10
    case 'pirate':   return S.pirateGreedPeakStacks;              // 0-25 (BETA 16.51)
    default:         return 0;
  }
}

export function getSignatureActiveSecondsThisRun(state: GameState): number {
  const charId = state.character?.id;
  switch (charId) {
    case 'wizard': return Math.round(S.wizSurgeSecondsThisRun * 10) / 10;
    default:       return 0;
  }
}

export function getSignatureBonusDamageThisRun(state: GameState): number {
  const charId = state.character?.id;
  // BETA 19.93 — all 10 chars now have signature-credit math.
  switch (charId) {
    case 'lebron':   return Math.round(S.lebronBonusDamageTotal);
    case 'killa':    return Math.round(S.killaSignatureBonusDmg);
    case 'pirate':   return Math.round(S.pirateSignatureBonusDmg);
    case 'wizard':   return Math.round(S.wizardSignatureBonusDmg);
    case 'viking':   return Math.round(S.vikingSignatureBonusDmg);
    case 'mr_silly': return Math.round(S.sillySignatureBonusDmg);
    case 'sponge':   return Math.round(S.spongeSignatureBonusDmg);
    case 'chad':     return Math.round(S.chadSignatureBonusDmg);
    case 'cowboy':   return Math.round(S.cowboySignatureBonusDmg);
    case 'tactical': return Math.round(S.tacticalSignatureBonusDmg);
    default:         return 0;
  }
}

export function getPassiveBonusFinalThisRun(state: GameState): number {
  const charId = state.character?.id;
  if (!charId) return 0;
  const def = CHARACTER_PASSIVES[charId];
  if (!def) return 0;
  const lvl = Math.max(1, state.player.level ?? 1);
  return def.layer1PerLevel * lvl;
}
